// @givmo/board — the getting-started list's tolerant decode and its words (CBG-YR4-START, -ABOUT;
// backend `agent_docs/contracts/board-onboarding.md` §3, §4).
//
// The list is working state over records. What makes an item done is a record the module already
// keeps — the member's `acknowledged` event on the document's bytes (or an officer's entry of a signed
// paper form), statement A of an annual answer for the policy, the person's annual response for the
// disclosure, the officer's recorded buddy — and the backend derives it on every read. So nothing
// here decides "done": each helper only puts the served facts into words.

import type {
  BoardGoverningDocumentKind,
  BoardMemberGettingStartedItemIn,
  BoardDirectorAboutOut,
  BoardDirectorGettingStartedItemOut,
  BoardDirectorGettingStartedOut,
  BoardGettingStartedConfirmationOut,
  BoardGettingStartedDisclosureOut,
  BoardGettingStartedListItemOut,
  BoardGettingStartedListOut,
  BoardMemberGettingStartedItemOut,
  BoardMemberGettingStartedOut,
} from '@givmo/api-client';
import { displayDate } from '../../lib/display';
import { shortDay } from '../conflicts/coi-decode';
import { documentKindLabel } from '../govdocs/govdocs-decode';
import { capacityLineOf, joinWords } from '../roster/officer-roles';
import { onboardingWords as w } from './onboarding-words';

/** Any item, whichever list it is on: the fields every one of them carries. */
export type AnyGettingStartedItem =
  | BoardGettingStartedListItemOut
  | BoardMemberGettingStartedItemOut
  | BoardDirectorGettingStartedItemOut;

/** One item as a write names it (the generated request item; a person's item keeps its `id`). */
export type ItemSpec = BoardMemberGettingStartedItemIn;

/** A served governing-document kind as the request's closed set names it. */
export function asDocumentKind(kind: string | null | undefined): BoardGoverningDocumentKind | null {
  switch (kind) {
    case 'bylaws':
    case 'coi_policy':
    case 'whistleblower_policy':
    case 'document_retention_policy':
    case 'committee_charter':
    case 'other':
      return kind;
    default:
      return null;
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function itemsOf<T>(value: unknown): T[] {
  return Array.isArray(value) ? (value.filter(isRecord) as T[]) : [];
}

/** The board's list, or Givmo's starting list (`adopted` false); `null` for any other shape. */
export function extractBoardList(body: unknown): BoardGettingStartedListOut | null {
  if (!isRecord(body) || typeof body.adopted !== 'boolean') return null;
  const list = body as BoardGettingStartedListOut;
  return { ...list, items: itemsOf<BoardGettingStartedListItemOut>(list.items) };
}

/** One person's list as an officer reads it; `null` for any other shape. */
export function extractMemberList(body: unknown): BoardMemberGettingStartedOut | null {
  if (!isRecord(body) || typeof body.started !== 'boolean') return null;
  const list = body as BoardMemberGettingStartedOut;
  return { ...list, items: itemsOf<BoardMemberGettingStartedItemOut>(list.items) };
}

/** The member's own list; `null` for any other shape. */
export function extractOwnList(body: unknown): BoardDirectorGettingStartedOut | null {
  if (!isRecord(body) || typeof body.started !== 'boolean') return null;
  const list = body as BoardDirectorGettingStartedOut;
  return { ...list, items: itemsOf<BoardDirectorGettingStartedItemOut>(list.items) };
}

/** About the board; `null` for any other shape. */
export function extractAbout(body: unknown): BoardDirectorAboutOut | null {
  if (!isRecord(body) || !Array.isArray(body.people)) return null;
  return body as BoardDirectorAboutOut;
}

/** An item's name: the document's own title in force, else its kind (and label); the steps' words. */
export function itemTitle(item: AnyGettingStartedItem): string {
  if (item.item_kind === 'disclosure') return w.disclosureTitle;
  if (item.item_kind === 'buddy') return w.buddyTitle;
  if (item.item_kind === 'step') return item.step_text ?? '';
  return documentName(item).name;
}

/** A document as the list names it, and whether that name is Board's word for it: its kind (then
 *  any label the board gave it), where it has no title of its own. A sentence reads Board's word in
 *  its own case and a title the board wrote as written (the to-do's "Read the …"). */
export interface DocumentName {
  name: string;
  byKind: boolean;
}

export function documentName(item: AnyGettingStartedItem): DocumentName {
  const title = (item.record_title ?? '').trim();
  if (title.length > 0) return { name: title, byKind: false };
  const kind = documentKindLabel(item.governing_document_kind);
  const label = (item.governing_document_label ?? '').trim();
  return { name: label.length > 0 ? `${kind} — ${label}` : kind, byKind: true };
}

/** The line under a read item's title: which version, or where the record comes from. */
export function itemDetail(item: AnyGettingStartedItem): string | null {
  if (item.item_kind !== 'read' || !item.target_occurred_on) return null;
  return item.governing_document_kind
    ? w.versionLine(displayDate(item.target_occurred_on))
    : w.recordLine(displayDate(item.target_occurred_on));
}

/** How a read item was confirmed, in words. */
export function confirmationWords(confirmation: BoardGettingStartedConfirmationOut): string {
  if (confirmation.road === 'paper')
    return w.paperOn(shortDay(confirmation.paper_signed_on ?? confirmation.acknowledged_at));
  if (confirmation.road === 'annual_disclosure') return w.annualOn(confirmation.cycle_label ?? '');
  return w.confirmedOn(shortDay(confirmation.acknowledged_at));
}

/** The disclosure item's stage, in words (the officer's card; the member reads its own lines). */
export function disclosureWords(
  disclosure: BoardGettingStartedDisclosureOut | null | undefined,
): string {
  const label = disclosure?.label ?? '';
  switch (disclosure?.stage) {
    case 'answered':
      return w.disclosureAnswered(label, shortDay(disclosure.responded_at));
    case 'sent':
      return w.disclosureSentNotAnswered(label, shortDay(disclosure.sent_at));
    case 'not_sent':
      return w.disclosureNotSent(label);
    default:
      return w.disclosureNextYear;
  }
}

/** One item's state in plain words, and its tone: `quiet` while it waits on someone. */
export function itemStatus(
  item: BoardMemberGettingStartedItemOut,
  buddyName: string | null,
): { word: string; tone: 'plain' | 'quiet' } {
  if (item.item_kind === 'read') {
    if (item.confirmation) return { word: confirmationWords(item.confirmation), tone: 'plain' };
    if (item.available === false) return { word: w.unavailable, tone: 'quiet' };
    return { word: w.notYet, tone: 'quiet' };
  }
  if (item.item_kind === 'disclosure')
    return { word: disclosureWords(item.disclosure), tone: item.done ? 'plain' : 'quiet' };
  if (item.item_kind === 'buddy')
    return buddyName ? { word: buddyName, tone: 'plain' } : { word: w.buddyNone, tone: 'quiet' };
  return item.step_done_on
    ? { word: w.doneOn(shortDay(item.step_done_on)), tone: 'plain' }
    : { word: w.notYet, tone: 'quiet' };
}

/** The documents a signed paper form can still record: read items in force and not yet confirmed. */
export function paperCandidates(
  items: readonly BoardMemberGettingStartedItemOut[],
): BoardMemberGettingStartedItemOut[] {
  return items.filter(
    (item) =>
      item.item_kind === 'read' &&
      !item.done &&
      item.available !== false &&
      Boolean(item.target_record_id) &&
      Boolean(item.target_content_hash),
  );
}

/** A member's read items still to read: in force, not confirmed. */
export function stillToRead(items: readonly AnyGettingStartedItem[]): AnyGettingStartedItem[] {
  return items.filter(
    (item) =>
      item.item_kind === 'read' && item.available !== false && !('done' in item && item.done),
  );
}

/** "Maren Lindqvist, Secretary and Treasurer": the buddy's titles, else "Director" (frame g5). */
export function buddyCapacity(buddy: {
  is_director?: boolean | null;
  title_labels?: readonly string[] | null;
}): string | null {
  const titles = (buddy.title_labels ?? []).filter((label) => label.trim().length > 0);
  if (titles.length > 0) return joinWords(titles);
  return buddy.is_director ? capacityLineOf(true, []) : null;
}

/** An item as a write names it, keeping a person's item's id when `keepId`. */
export function toSpec(item: AnyGettingStartedItem, keepId: boolean): ItemSpec {
  const kind = item.item_kind as ItemSpec['item_kind'];
  const spec: ItemSpec = { item_kind: kind };
  if (kind === 'read') {
    if (item.governing_document_kind) {
      spec.governing_document_kind = asDocumentKind(item.governing_document_kind);
      spec.governing_document_label = item.governing_document_label ?? null;
    } else {
      spec.record_id = 'record_id' in item ? (item.record_id ?? null) : null;
    }
  }
  if (kind === 'step') spec.step_text = item.step_text ?? '';
  if (keepId && 'id' in item && typeof item.id === 'string' && item.id.length > 0)
    spec.id = item.id;
  return spec;
}

/** Each read item's title as the list shows it, keyed as the editor looks it up: a record by its
 *  id, a governing document by its chain key. */
export function titlesOf(items: readonly AnyGettingStartedItem[]): Map<string, string> {
  const titles = new Map<string, string>();
  for (const item of items) {
    if (item.item_kind !== 'read') continue;
    if (item.governing_document_kind)
      titles.set(
        chainKey(item.governing_document_kind, item.governing_document_label),
        itemTitle(item),
      );
    else if ('record_id' in item && item.record_id) titles.set(item.record_id, itemTitle(item));
  }
  return titles;
}

/** A governing document's chain key, for the editor's "already on the list" rule: its kind and its
 *  exact label, as the backend's registry keys a chain ("Finance" and "finance" are two documents). */
export function chainKey(
  kind: string | null | undefined,
  label: string | null | undefined,
): string {
  return `${kind ?? ''}::${(label ?? '').trim()}`;
}
